import type { AxiosError, AxiosResponse } from "axios";
import {
  getCookie,
  hasWindow,
  isDebugAPI,
  logErrorToSentry,
} from "@litres/utils";
import type {
  IGlobalApiParams,
  IReduxRequestNode,
  IGUJResponse,
  IResponse,
  IResponseErrorDetails,
} from "@litres-libs/types";
import axios from "../../instance/axios";
import type { ICsrfConfig } from "../config/csrf";
import { csrfConfig } from "../config/csrf";
import { parseGUAjaxJSONfromHTML } from "../utils/GUAjaxUtils";
import { TroublePopup } from "../utils/TroublePopup";
import { ApiError, ApiType } from "../../types/api";

/**
 * Провайдер запросов GUJ/GUJ.
 * Создает синглтон, чтобы все запросы шли через 1 точку
 */
export class GUJPromiseProvider<TParams, TPayload> {
  private constructor(config: ICsrfConfig, initialSID: string | undefined) {
    this.SID = initialSID || undefined;
    this.baseURL = config.baseURL || "DEFAULT_BASE_URL";
    this.troublePopup = null;
  }

  private SID: string | undefined;

  private baseURL: string;

  public troublePopup: TroublePopup | null;

  private static instance: GUJPromiseProvider<any, any>;

  /**
   * Метод, возвращающий единичный экземпляр синглтона
   * @returns Возвращает провайдер каталита для вызова запросов
   */
  public static getInstance<TParams, TPayload>(): GUJPromiseProvider<
    TParams,
    TPayload
  > {
    if (!GUJPromiseProvider.instance) {
      // Передачу конфига не делаем, рассчитываем что мы работаем с одним каталитом по одному конфигу
      // По умолчанию берем SID из куки, но в дальнейшем дадим делать запрос от сида в параметрах запроса
      const initialSID = getCookie("SID");
      GUJPromiseProvider.instance = new GUJPromiseProvider(
        csrfConfig,
        initialSID
      );
    }

    return GUJPromiseProvider.instance;
  }

  /**
   * Основной метод вызова запроса
   * @param params - стандартные передаваемые параметры для всех запросов в любые наши API
   * @returns {Promise} - Возвращает пропис запроса каталита
   */
  public async makeAxiosRequest(
    params: IGlobalApiParams<TParams>
  ): Promise<IReduxRequestNode<TParams, TPayload>> {
    // GUJ отправляет данные запроса в виде formData, сформируем из объекта
    const bodyFormData = new FormData();
    const dataset = params.request?.data as Record<string, unknown>;

    if (typeof dataset !== "undefined") {
      const keys = Object.keys(dataset);
      keys.forEach(key => {
        bodyFormData.append(key, String(dataset[key]));
      });
    }

    const url =
      params.url === "/"
        ? this.baseURL + "/pages/ajax_empty2/"
        : params.url && params.url.indexOf("http") === -1
          ? `${this.baseURL}${
              params.url === "/pages/ajax_empty2/"
                ? "/pages/ajax_empty2/"
                : params.url
            }`
          : params.url;
    const method = params.request ? params.request.method : "POST";
    const data = params.request ? bodyFormData : {};
    const _params = params.request ? params.request.params : {};

    // Делаем запрос
    const res = axios({
      url,
      method,
      data,
      params: _params,
      timeout: 40000,
    })
      .then((response: AxiosResponse<IGUJResponse<TPayload>>) => {
        const responseJson = parseGUAjaxJSONfromHTML<TPayload>(response.data);

        // Если не смогли спарсить строку с html - бросим обшибку
        if (responseJson === null) {
          throw {
            request: params,
            response: {
              status: 0,
              statusText: "Ответ не найден",
            },
          } as unknown as Error;
        }

        if (
          hasWindow &&
          (responseJson.need_captcha ||
            responseJson.actions_output?.need_captcha)
        ) {
          this.troublePopup = new TroublePopup({
            // УБрал передачу лоадер, так как в TroublePopup не было использования
            // loader: Loader,
            reason: "captcha",
          });

          this.troublePopup.init();
        }

        // Если в JSON нашли признак ошибки GUAjax - бросим ошибку
        if (responseJson?.error_code) {
          throw {
            request: params,
            response: {
              status: responseJson.error_code,
              statusText: responseJson.error_msg,
            },
          } as unknown as Error;
        }

        // Если в JSON нашли признак ошибки GUAjax - бросим ошибку
        if (responseJson?.error_msg) {
          throw {
            request: params,
            response: {
              status: responseJson.error_code,
              statusText: responseJson.error_msg,
            },
          } as unknown as Error;
        }

        if (isDebugAPI) {
          console.info(
            `%cRES: ${params.provider.id}`,
            "background: #abff5f; padding: 2px 12px; color: #848484",
            responseJson,
            params
          );
        }

        if (params.onSuccess) {
          params.onSuccess({
            request: params,
            response: responseJson,
          });
        }

        delete params.onError;
        delete params.onSuccess;

        // В случае успешного ответа вернем ответ в fullfill
        return {
          request: params,
          response: responseJson,
        };
      })
      .catch((error: AxiosError<IResponse>) => {
        if (error.response?.status === 500) {
          logErrorToSentry(new ApiError("API Error (500)"), {
            contextProperties: [
              "REQUEST_PARAMS",
              {
                url,
                method,
                params: JSON.stringify(_params),
                data: JSON.stringify(data),
              },
            ],
            tags: {
              api_name: ApiType.GUJ,
            },
          });
        }

        if (isDebugAPI) {
          console.error(
            `%cERR: ${params.provider.id}`,
            "background: #f55e5e; padding: 2px 12px; color: #fff",
            error
          );
        }

        if (params.onError) {
          params.onError({
            request: params,
            error: {
              status: error.response?.status,
              type: (error.response?.data.error as IResponseErrorDetails).type,
              title: (error.response?.data.error as IResponseErrorDetails)
                .title,
            },
          });
        }

        delete params.onError;
        delete params.onSuccess;

        return {
          request: params,
          error: {
            status: error.response?.status,
            type: (error.response?.data.error as IResponseErrorDetails).type,
            title: (error.response?.data.error as IResponseErrorDetails).title,
          },
        };
      });

    return res as Promise<IReduxRequestNode<TParams, TPayload>>;
  }
}
